import i18n from 'i18next';
import { action, makeObservable, observable } from 'mobx';

import { CONTACT_SUPPORT_URL } from '../../config/constants';
import { getUserLanguage } from '../../i18n/helpers';
import { getUserReadableName } from '../../utils/getUserReadableName';
import type { Profile } from '../auth';
import { PlatformEnum } from '../platformContext/platformContextTypes';

const APP_ID = 'nval56bo';
const API_BASE = 'https://api-iam.intercom.io';

class Intercom {
  private prevLang = getUserLanguage();

  // The Messenger open over the lobby: popups must not open on top of it (useFeatureManager, PLT-992).
  public isOpen = false;

  private _settings: IntercomBootProps = {
    api_base: API_BASE,
    app_id: APP_ID,
    custom_launcher_selector: '.support-button',
  };

  constructor() {
    makeObservable<Intercom, 'setOpen'>(this, { isOpen: observable, setOpen: action });

    i18n.on('languageChanged', (rawLang) => {
      const lang = getUserLanguage(rawLang);

      if (this.prevLang !== lang) {
        this.prevLang = lang;
        this.updateUser({
          language_override: lang.toLowerCase(),
        });
      }
    });
  }

  private _createUser(profile: Profile | null): Partial<IntercomUpdateProps> {
    if (!profile?.intercomHash) {
      return {
        user_type: 'lead',
      };
    }

    return {
      name: getUserReadableName(profile) ?? undefined,
      email: profile.email,
      user_id: profile.uniqueId,
      user_hash: profile.intercomHash,
      brand: 'crowncoins',
      platform: PlatformEnum.WEB,
      user_type: 'user',
    };
  }

  private _loadIntercom(profile: Profile | null) {
    if (!window.Intercom) {
      return;
    }

    const settings: IntercomBootProps = {
      ...this._settings,
      ...this._createUser(profile),
      last_request_at: new Date().getTime() / 1000,
      language_override: getUserLanguage().toLowerCase(),
      brand: 'crowncoins',
      platform: PlatformEnum.WEB,
    };

    window.intercomSettings = settings;
    window.Intercom('boot', settings);
    window.Intercom('onShow', () => this.setOpen(true));
    window.Intercom('onHide', () => this.setOpen(false));
  }

  private setOpen(isOpen: boolean) {
    this.isOpen = isOpen;
  }

  private updateUser(props: IntercomUpdateProps) {
    if (!window.Intercom) {
      return;
    }
    window.Intercom('update', props);
  }

  public updateUserData(profile: Profile) {
    const props = this._createUser(profile);
    this.updateUser(props);
  }

  // clears intercom session
  private shutdown() {
    if (!window.Intercom) {
      return;
    }
    window.Intercom('shutdown');
    this.setOpen(false);
  }

  private show() {
    if (!window.Intercom) {
      return;
    }
    window.Intercom('show');
  }

  public identifyUser(profile: Profile) {
    if (!window.Intercom) {
      return;
    }
    this.shutdown();
    this._loadIntercom(profile);
    this.updateUserData(profile);
  }

  public restartSession(profile: Profile | null) {
    if (!window.Intercom) {
      return;
    }
    this.shutdown();
    this.init(profile);
  }

  public init(profile: Profile | null) {
    if (!window.Intercom) {
      return;
    }
    this._loadIntercom(profile);
  }

  public handleOpenSupport = () => {
    if (!window.Intercom) {
      window.open(CONTACT_SUPPORT_URL, '_blank');
      return;
    }
    setTimeout(() => {
      this.show();
    }, 100);
  };
}
export const intercom = new Intercom();
